var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { selectTool, bringManipulativesToFront } from '../helper/helper';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce } from '../helper/announcer';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { NF_CELL_SIZE } from '../constants/gridConstants';
import ButtonComponent from '../components/ButtonComponent';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import ToolTip from '../components/ToolTip';
import { calculateTokenPosition } from '../helper/tokenDropHelper';
import { t } from 'i18next';
import '../styles/widgets/numberFrames.scss';
var CELL_SIZE = NF_CELL_SIZE;
var GRID_PADDING = 0; // No exterior padding around the grid
// Reliable touch-primary detection — iPad Safari still fires mouseover after
// taps, so we gate hover-only affordances (tooltip portals) behind this.
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var NumberFrames = function (_a) {
    var _b, _c, _d;
    var _e, _f, _g, _h, _j, _k;
    var id = _a.id, data = _a.data;
    var center = data.center, rows = data.rows, cols = data.cols, _l = data.rotation, rotation = _l === void 0 ? 0 : _l, _m = data.scale, scale = _m === void 0 ? 1 : _m, _o = data.show5x5Grid, show5x5Grid = _o === void 0 ? false : _o, lock = data.lock;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.manipulativeNumberFrame'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeNumberFrame'));
    var _p = useContext(DataContext), manipulative = _p.manipulative, setManipulative = _p.setManipulative, selectedTool = _p.selectedTool, setSelectedTool = _p.setSelectedTool, panningEnable = _p.panningEnable, setManiplulatorComponentVisible = _p.setManiplulatorComponentVisible, setActiveManipulator = _p.setActiveManipulator, setSelectedManipulativeId = _p.setSelectedManipulativeId, contextMenu = _p.contextMenu, setContextMenu = _p.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var btnConfig = globalStore.useStoreState(function (st) { return st.jsonDataStore; }).btnConfig;
    var _q = useState(null), dragStart = _q[0], setDragStart = _q[1];
    var _r = useState(false), selectBtnHovered = _r[0], setSelectBtnHovered = _r[1];
    var _s = useState(null), selectBtnRect = _s[0], setSelectBtnRect = _s[1];
    var _t = useState(false), moveBtnHovered = _t[0], setMoveBtnHovered = _t[1];
    var _u = useState(null), moveBtnRect = _u[0], setMoveBtnRect = _u[1];
    var dragStartRef = useRef(null);
    var dragStartCenterSingle = useRef({ x: 0, y: 0 });
    var attachedCounterStartsSingle = useRef([]);
    var hasMoved = useRef(false);
    // Multi-select move refs
    var multiSelectStartPositions = useRef([]);
    var dragStartCenter = useRef({ x: 0, y: 0 });
    var dragFinalPosition = useRef({ x: 0, y: 0 });
    var selectedToolRef = useRef(selectedTool);
    var manipulativeRef = useRef(manipulative);
    var centerRef = useRef(center);
    useLayoutEffect(function () {
        if (!selectBtnHovered)
            return;
        var btn = document.querySelector("[data-id=\"".concat(id, "\"] .nfSelectBtn button"));
        if (btn)
            setSelectBtnRect(btn.getBoundingClientRect());
    }, [zoomLevel, scale, selectBtnHovered, id]);
    // Mirror the select-overlay hover→tooltip pattern for the move handle so the
    // tooltip position tracks zoom / scale changes while hovered.
    useLayoutEffect(function () {
        if (!moveBtnHovered)
            return;
        var btn = document.querySelector("[data-id=\"".concat(id, "\"] .nfMoveHandle button"));
        if (btn)
            setMoveBtnRect(btn.getBoundingClientRect());
    }, [zoomLevel, scale, moveBtnHovered, id]);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    useEffect(function () {
        centerRef.current = center;
    }, [center]);
    var isSelected = selectedTool.includes(id);
    // Only show inline rotate/move handles when the selection is exclusively NumberFrames.
    // If mixed with other item types, ResizerComponent is still active — avoid double handles.
    var selectionIsAllFrames = isSelected;
    // Mirror of the dice pattern: the frame wrapper becomes Tab-focusable once the
    // frame is the selected manipulative. Tab lands on the `nfSelectBtn`
    // still fall back on this wrapper-focus effect.
    // and the effect below moves focus here, so a second Enter opens the
    // context menu. `skipWrapperFocusRef` is kept as an escape hatch for any
    // future selection path that wants to bypass this auto-focus.
    var frameInteractiveRef = useRef(null);
    var wasSelectedRef = useRef(false);
    var skipWrapperFocusRef = useRef(false);
    useEffect(function () {
        var wasSelected = wasSelectedRef.current;
        wasSelectedRef.current = isSelected;
        if (!isSelected || wasSelected)
            return;
        if (panningEnable || contextMenu)
            return;
        if (skipWrapperFocusRef.current) {
            skipWrapperFocusRef.current = false;
            return;
        }
        // Brief delay matches the dice pattern — SelectFocusOverlay unmounts first.
        var timer = setTimeout(function () {
            var _a;
            (_a = frameInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }, 100);
        return function () { return clearTimeout(timer); };
    }, [isSelected, panningEnable, contextMenu]);
    // Enter / Space / ContextMenu / Shift+F10 → open the context menu (same keys
    // the dice widget responds to).
    var handleFrameKeyDown = function (e) {
        var _a;
        if (panningEnable)
            return;
        var opensMenu = e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10');
        if (opensMenu) {
            e.preventDefault();
            e.stopPropagation();
            if (!selectedTool.includes(id)) {
                setSelectedTool([id]);
            }
            var rect = (_a = frameInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: id,
                });
            }
            return;
        }
        // Arrow-key nudge — moves the frame and every attached counter by `step`
        // pixels. Shift multiplies the step for coarse movement. Skipped when the
        // frame's position is locked.
        var arrows = {
            ArrowUp: { x: 0, y: -1, dir: 'up' },
            ArrowDown: { x: 0, y: 1, dir: 'down' },
            ArrowLeft: { x: -1, y: 0, dir: 'left' },
            ArrowRight: { x: 1, y: 0, dir: 'right' },
        };
        var nudge = arrows[e.key];
        if (!nudge || lock.positioning)
            return;
        e.preventDefault();
        e.stopPropagation();
        var step = e.shiftKey ? 50 : 10;
        var dx = nudge.x * step;
        var dy = nudge.y * step;
        var oldCenter = { x: center.x, y: center.y };
        var newCenter = { x: oldCenter.x + dx, y: oldCenter.y + dy };
        var attachedCounters = manipulativeRef.current.filter(function (m) {
            return m.data.type === 'numberFrameCounter' &&
                m.data.frameId === id;
        });
        var nudgeBatch = __spreadArray([
            {
                type: id,
                id: id,
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            }
        ], attachedCounters.map(function (m) {
            var c = m.data;
            return {
                type: c.id,
                id: c.id,
                propertyName: 'center',
                old: JSON.stringify(c.center),
                new: JSON.stringify({ x: c.center.x + dx, y: c.center.y + dy }),
            };
        }), true);
        saveState(nudgeBatch);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id === id && item.data.type === 'numberFrames') {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) });
                }
                if (item.data.type === 'numberFrameCounter') {
                    var c = item.data;
                    if (c.frameId === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, c), { center: { x: c.center.x + dx, y: c.center.y + dy } }) });
                    }
                }
                return item;
            });
        });
        announce(t('text.manipulativeMoved', {
            name: t('text.manipulativeNumberFrame'),
            direction: nudge.dir,
            x: Math.round(newCenter.x),
            y: Math.round(newCenter.y),
        }));
    };
    // Right-click inside the frame. If the click lands on a cell (or its
    // gap/border) that contains a token, we open the counter's context menu
    // instead of the frame's — this gives the counter a wider hit area than
    // just the 50×50 shape inside the 55×55 cell. Counter's own onContextMenu
    // already fires + stopPropagation when the click is on the shape, so this
    // only runs for the surrounding cell area.
    var handleFrameContextMenu = function (e) {
        var _a;
        if (panningEnable)
            return;
        var target = e.target;
        var cellEl = target.closest('[data-cell-index]');
        if (cellEl) {
            var idxAttr = cellEl.getAttribute('data-cell-index');
            var cellIdx = idxAttr !== null ? parseInt(idxAttr, 10) : NaN;
            var tokenId = !Number.isNaN(cellIdx) ? (_a = data.tokens) === null || _a === void 0 ? void 0 : _a[cellIdx] : undefined;
            if (tokenId) {
                e.preventDefault();
                e.stopPropagation();
                if (!selectedTool.includes(tokenId))
                    setSelectedTool([tokenId]);
                setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    manipulativeId: tokenId,
                });
                return;
            }
        }
        // Empty cell (or click outside any cell) → open the frame's own menu.
        e.preventDefault();
        e.stopPropagation();
        if (!selectedTool.includes(id))
            setSelectedTool([id]);
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            manipulativeId: id,
        });
    };
    // Inline rotate handle state (replaces ResizerComponent for NumberFrames)
    var rotateIsDragging = useRef(false);
    var _v = useState(false), rotateGrabbing = _v[0], setRotateGrabbing = _v[1];
    var rotateStartRotation = useRef(0);
    var mainHolderRectRef = useRef(null);
    // Captures the final snapped rotation so saveState can be called outside the updater
    var rotateSnapRef = useRef(0);
    var handleRotateMouseDown = function (e) {
        e.preventDefault();
        e.stopPropagation();
        rotateIsDragging.current = true;
        setRotateGrabbing(true);
        rotateStartRotation.current = rotation;
        var mainHolder = document.getElementsByClassName('mainHolder')[0];
        mainHolderRectRef.current = mainHolder ? mainHolder.getBoundingClientRect() : null;
    };
    // Keyboard rotate: arrow keys rotate by 1° (15° with Shift), called from nfRotateBtn
    var handleRotateKeyDown = function (e) {
        var clockwise = e.key === 'ArrowRight' || e.key === 'ArrowUp';
        var anticlockwise = e.key === 'ArrowLeft' || e.key === 'ArrowDown';
        if (!clockwise && !anticlockwise)
            return;
        e.preventDefault();
        e.stopPropagation();
        var step = e.shiftKey ? 15 : 1;
        var delta = clockwise ? step : -step;
        setManipulative(function (prev) {
            var frameItem = prev.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; });
            if (!frameItem)
                return prev;
            var nf = frameItem.data;
            var newRotation = ((nf.rotation || 0) + delta + 360) % 360;
            var deltaRad = (delta * Math.PI) / 180;
            return prev.map(function (item) {
                if (item.data.id === id && item.data.type === 'numberFrames') {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, nf), { rotation: newRotation }) });
                }
                if (item.data.type === 'numberFrameCounter' && item.data.frameId === id) {
                    var counter = item.data;
                    var dx = counter.center.x - nf.center.x;
                    var dy = counter.center.y - nf.center.y;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: {
                                x: nf.center.x + dx * Math.cos(deltaRad) - dy * Math.sin(deltaRad),
                                y: nf.center.y + dx * Math.sin(deltaRad) + dy * Math.cos(deltaRad),
                            }, rotation: 0 }) });
                }
                return item;
            });
        });
    };
    var handleRotateKeyUp = function (e) {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key))
            return;
        // Read current rotation from manipulativeRef to avoid stale prop closure
        var frameItem = manipulativeRef.current.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; });
        var currentRotation = frameItem ? frameItem.data.rotation || 0 : rotation;
        saveState({
            type: id,
            id: id,
            propertyName: 'rotation',
            old: JSON.stringify(rotateStartRotation.current),
            new: JSON.stringify(currentRotation),
        });
        var direction = currentRotation > rotateStartRotation.current
            ? t('text.clockwise', { defaultValue: 'clockwise' })
            : t('text.anticlockwise', { defaultValue: 'anticlockwise' });
        announce(t('text.annotationRotated', {
            name: t('text.manipulativeNumberFrame'),
            degrees: currentRotation,
            direction: direction,
        }));
        rotateStartRotation.current = currentRotation;
    };
    useEffect(function () {
        if (!isSelected)
            return;
        var onMove = function (e) {
            if (!rotateIsDragging.current || !mainHolderRectRef.current)
                return;
            e.preventDefault();
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var rect = mainHolderRectRef.current;
            var angle = Math.atan2(clientY - rect.y - centerRef.current.y * zoomLevel, clientX - rect.x - centerRef.current.x * zoomLevel);
            var newRotation = ((angle * 180) / Math.PI + 90 + 360) % 360;
            // Compute rotationDelta inside the updater from `prev` to avoid stale closure issues
            setManipulative(function (prev) {
                var frameIdx = prev.findIndex(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; });
                if (frameIdx === -1)
                    return prev;
                var currentRotation = prev[frameIdx].data.rotation || 0;
                var rotationDelta = newRotation - currentRotation;
                if (rotationDelta > 180)
                    rotationDelta -= 360;
                if (rotationDelta < -180)
                    rotationDelta += 360;
                var updatedFrame = __assign(__assign({}, prev[frameIdx].data), { rotation: newRotation });
                var updated = __spreadArray([], prev, true);
                updated[frameIdx] = __assign(__assign({}, updated[frameIdx]), { data: updatedFrame });
                updated = updated.map(function (item) {
                    var _a;
                    if (item.data.type === 'numberFrameCounter' && item.data.frameId === id) {
                        var counter_1 = item.data;
                        var cellIndex = (_a = Object.entries(updatedFrame.tokens || {}).find(function (_a) {
                            var v = _a[1];
                            return v === counter_1.id;
                        })) === null || _a === void 0 ? void 0 : _a[0];
                        var newCenter = cellIndex !== undefined
                            ? calculateTokenPosition(updatedFrame, parseInt(cellIndex))
                            : counter_1.center;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, counter_1), { center: newCenter, rotation: newRotation }) });
                    }
                    return item;
                });
                return updated;
            });
        };
        var onUp = function () {
            var _a;
            if (!rotateIsDragging.current)
                return;
            rotateIsDragging.current = false;
            setRotateGrabbing(false);
            mainHolderRectRef.current = null;
            // Snap to nearest 90°. saveState is called after setManipulative to avoid
            // side effects inside the functional updater (StrictMode double-invocation).
            setManipulative(function (prev) {
                var frameItem = prev.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; });
                if (!frameItem)
                    return prev;
                var nf = frameItem.data;
                var snapped = Math.round((nf.rotation || 0) / 90) * 90;
                rotateSnapRef.current = snapped;
                var snappedFrame = __assign(__assign({}, nf), { rotation: snapped });
                return prev.map(function (item) {
                    var _a;
                    if (item.data.id === id && item.data.type === 'numberFrames') {
                        return __assign(__assign({}, item), { data: snappedFrame });
                    }
                    if (item.data.type === 'numberFrameCounter' && item.data.frameId === id) {
                        var counter_2 = item.data;
                        var cellIndex = (_a = Object.entries(snappedFrame.tokens || {}).find(function (_a) {
                            var v = _a[1];
                            return v === counter_2.id;
                        })) === null || _a === void 0 ? void 0 : _a[0];
                        var newCenter = cellIndex !== undefined
                            ? calculateTokenPosition(snappedFrame, parseInt(cellIndex))
                            : counter_2.center;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, counter_2), { center: newCenter, rotation: snapped }) });
                    }
                    return item;
                });
            });
            // Batch-save counter centers alongside rotation so locked counters undo correctly
            var currentFrame = (_a = manipulativeRef.current.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; })) === null || _a === void 0 ? void 0 : _a.data;
            var snappedVal = rotateSnapRef.current;
            var snappedFrameForBatch = currentFrame ? __assign(__assign({}, currentFrame), { rotation: snappedVal }) : null;
            var rotateDragBatch = [
                {
                    type: id,
                    id: id,
                    propertyName: 'rotation',
                    old: JSON.stringify(rotateStartRotation.current),
                    new: JSON.stringify(snappedVal),
                },
            ];
            if (snappedFrameForBatch) {
                manipulativeRef.current
                    .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === id; })
                    .forEach(function (m) {
                    var _a;
                    var c = m.data;
                    var cellIndex = (_a = Object.entries(snappedFrameForBatch.tokens || {}).find(function (_a) {
                        var v = _a[1];
                        return v === c.id;
                    })) === null || _a === void 0 ? void 0 : _a[0];
                    var newCenter = cellIndex !== undefined
                        ? calculateTokenPosition(snappedFrameForBatch, parseInt(cellIndex))
                        : c.center;
                    rotateDragBatch.push({
                        type: c.id,
                        id: c.id,
                        propertyName: 'center',
                        old: JSON.stringify(c.center),
                        new: JSON.stringify(newCenter),
                    });
                });
            }
            saveState(rotateDragBatch);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [isSelected, id, zoomLevel, setManipulative]);
    var GRID_BORDER_PX = 2; // matches .grid { border: 2px } in numberFrames.scss
    var width = cols * CELL_SIZE + GRID_PADDING * 2;
    var height = rows * CELL_SIZE + GRID_PADDING * 2;
    // Full outer size including the grid border so transform-origin: center center
    // rotates around frame.center exactly, not offset by the border width.
    var outerWidth = width + GRID_BORDER_PX * 2;
    var outerHeight = height + GRID_BORDER_PX * 2;
    // Drag handlers
    var onMouseDown = function (e) {
        if (lock.positioning || panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Select. Bring-to-front is CSS-only while selected/dragging (see the
        // wrapper style below) — the manipulative array is only permanently
        // reordered on drop, in onUp, so a plain click never touches array order.
        setSelectedTool(selectTool(e, id, selectedToolRef.current, false));
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        dragStartRef.current = { x: clientX, y: clientY };
        dragStartCenterSingle.current = { x: center.x, y: center.y };
        attachedCounterStartsSingle.current = manipulative
            .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === id; })
            .map(function (m) { return ({ id: m.data.id, center: __assign({}, m.data.center) }); });
        hasMoved.current = false;
        setDragStart({
            x: clientX - center.x * zoomLevel,
            y: clientY - center.y * zoomLevel,
        });
        // The frame boosts its own zIndex to 9999 while dragging (below), but its attached
        // counters are separate, independently-positioned manipulatives — not DOM children of
        // the frame — so without their own matching boost they render BEHIND the dragged
        // frame. Tell them a drag just started so each attached counter can raise its own
        // zIndex for the duration (see the 'numberFrameDragActive' listener in Counter.tsx).
        customEventHandler.dispatchEvent('numberFrameDragActive', { frameId: id, active: true });
        // Multi-select: capture start positions of all selected NumberFrames and Counters
        if (selectedTool.length > 1 && selectedTool.includes(id)) {
            dragStartCenter.current = { x: center.x, y: center.y };
            multiSelectStartPositions.current = manipulative
                .filter(function (item) {
                return item.data.id !== undefined &&
                    selectedTool.includes(item.data.id) &&
                    (item.data.type === 'numberFrames' || item.data.type === 'numberFrameCounter');
            })
                .map(function (item) { return ({ id: item.data.id, center: __assign({}, item.data.center) }); });
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
        }
    };
    // Drag move and end
    useEffect(function () {
        var onMove = function (e) {
            if (!dragStartRef.current || !dragStart)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved.current = true;
            }
            if (!hasMoved.current)
                return;
            var newX = (clientX - dragStart.x) / zoomLevel;
            var newY = (clientY - dragStart.y) / zoomLevel;
            var deltaX = newX - centerRef.current.x;
            var deltaY = newY - centerRef.current.y;
            // Track final position for batch saveState on drag end
            dragFinalPosition.current = { x: newX, y: newY };
            setManipulative(function (prev) {
                var _a;
                var frameItem = (_a = prev.find(function (m) { return m.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data;
                var updatedFrame = frameItem
                    ? __assign(__assign({}, frameItem), { center: { x: newX, y: newY } }) : undefined;
                return prev.map(function (item) {
                    // Move the frame
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) });
                    }
                    // Move all counters attached to this frame (including locked ones)
                    if (item.data.type === 'numberFrameCounter') {
                        var counter_3 = item.data;
                        if (counter_3.frameId === id && updatedFrame) {
                            var cellEntry = Object.entries(updatedFrame.tokens || {}).find(function (_a) {
                                var v = _a[1];
                                return v === counter_3.id;
                            });
                            var newCenter = cellEntry
                                ? calculateTokenPosition(updatedFrame, parseInt(cellEntry[0]))
                                : { x: counter_3.center.x + deltaX, y: counter_3.center.y + deltaY };
                            return __assign(__assign({}, item), { data: __assign(__assign({}, counter_3), { center: newCenter }) });
                        }
                    }
                    return item;
                });
            });
            // Multi-select: broadcast delta so other selected items follow
            if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: newX - dragStartCenter.current.x,
                    deltaY: newY - dragStartCenter.current.y,
                    sourceId: id,
                });
            }
        };
        var onUp = function () {
            var wasMoving = hasMoved.current;
            var finalPos = dragFinalPosition.current;
            dragStartRef.current = null;
            setDragStart(null);
            hasMoved.current = false;
            // Single-select: save frame + attached counter position changes
            if (wasMoving && selectedToolRef.current.length <= 1) {
                var deltaX_1 = finalPos.x - dragStartCenterSingle.current.x;
                var deltaY_1 = finalPos.y - dragStartCenterSingle.current.y;
                var batchEntries = __spreadArray([
                    {
                        type: id,
                        id: id,
                        propertyName: 'center',
                        old: JSON.stringify(dragStartCenterSingle.current),
                        new: JSON.stringify(finalPos),
                    }
                ], attachedCounterStartsSingle.current.map(function (cs) { return ({
                    type: cs.id,
                    id: cs.id,
                    propertyName: 'center',
                    old: JSON.stringify(cs.center),
                    new: JSON.stringify({ x: cs.center.x + deltaX_1, y: cs.center.y + deltaY_1 }),
                }); }), true);
                // Bring the frame (and its attached tokens) to front by moving them to
                // the end of the manipulative array — only committed here, on drop
                // after a real drag. A plain click never reorders the array (CSS
                // zIndex handles the click/drag-in-progress visual boost instead).
                var idsToPromote = __spreadArray([id], attachedCounterStartsSingle.current.map(function (cs) { return cs.id; }), true);
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var reordered_1 = bringManipulativesToFront(manipulativeRef.current, idsToPromote);
                var afterIds = reordered_1.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                if (afterIds.join('|') !== beforeIds.join('|')) {
                    batchEntries.push({
                        type: id,
                        id: id,
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                    setManipulative(function () { return reordered_1; });
                }
                saveState(batchEntries);
            }
            // Multi-select: batch save all selected items' position changes in one undo step
            if (wasMoving && selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
                var selfStart = multiSelectStartPositions.current.find(function (p) { return p.id === id; });
                if (selfStart) {
                    var selfDeltaX_1 = dragFinalPosition.current.x - selfStart.center.x;
                    var selfDeltaY_1 = dragFinalPosition.current.y - selfStart.center.y;
                    var batchSave = __spreadArray(__spreadArray([], multiSelectStartPositions.current.map(function (startItem) { return ({
                        type: startItem.id,
                        id: startItem.id,
                        propertyName: 'center',
                        old: JSON.stringify(startItem.center),
                        new: JSON.stringify({
                            x: startItem.center.x + selfDeltaX_1,
                            y: startItem.center.y + selfDeltaY_1,
                        }),
                    }); }), true), attachedCounterStartsSingle.current
                        .filter(function (cs) { return !multiSelectStartPositions.current.some(function (p) { return p.id === cs.id; }); })
                        .map(function (cs) { return ({
                        type: cs.id,
                        id: cs.id,
                        propertyName: 'center',
                        old: JSON.stringify(cs.center),
                        new: JSON.stringify({ x: cs.center.x + selfDeltaX_1, y: cs.center.y + selfDeltaY_1 }),
                    }); }), true);
                    // Bring the whole multi-selected group to front together on drop.
                    var idsToPromote = __spreadArray(__spreadArray([], multiSelectStartPositions.current.map(function (p) { return p.id; }), true), attachedCounterStartsSingle.current
                        .filter(function (cs) { return !multiSelectStartPositions.current.some(function (p) { return p.id === cs.id; }); })
                        .map(function (cs) { return cs.id; }), true);
                    var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                    var reordered_2 = bringManipulativesToFront(manipulativeRef.current, idsToPromote);
                    var afterIds = reordered_2.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                    if (afterIds.join('|') !== beforeIds.join('|')) {
                        batchSave.push({
                            type: id,
                            id: id,
                            propertyName: 'reorder',
                            old: JSON.stringify(beforeIds),
                            new: JSON.stringify(afterIds),
                        });
                        setManipulative(function () { return reordered_2; });
                    }
                    if (!registerMultiSelectSaveEntries(id, batchSave)) {
                        saveState(batchSave);
                    }
                }
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
            }
            customEventHandler.dispatchEvent('numberFrameDragActive', { frameId: id, active: false });
        };
        if (dragStart) {
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend', onUp);
            window.addEventListener('touchcancel', onUp);
        }
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
        };
    }, [dragStart, setManipulative, id, zoomLevel]);
    // Multi-select listener: respond when another selected item is dragged
    useEffect(function () {
        if (!selectedTool.includes(id) || selectedTool.length <= 1)
            return;
        var myStart = { x: centerRef.current.x, y: centerRef.current.y };
        var attachedCounterStarts = [];
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            myStart = { x: centerRef.current.x, y: centerRef.current.y };
            attachedCounterStarts = manipulativeRef.current
                .filter(function (item) {
                return item.data.id !== undefined &&
                    item.data.type === 'numberFrameCounter' &&
                    item.data.frameId === id;
            })
                .map(function (item) { return ({ id: item.data.id, center: __assign({}, item.data.center) }); });
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId;
            if (sourceId === id || lock.positioning)
                return;
            var newX = myStart.x + deltaX;
            var newY = myStart.y + deltaY;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) });
                    }
                    // Also move all attached counters (including locked ones); skip sourceId counter to avoid double-move
                    if (item.data.type === 'numberFrameCounter') {
                        var counter = item.data;
                        if (counter.frameId === id && item.data.id !== sourceId) {
                            var counterStart = attachedCounterStarts.find(function (s) { return s.id === item.data.id; });
                            if (counterStart) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: {
                                            x: counterStart.center.x + deltaX,
                                            y: counterStart.center.y + deltaY,
                                        } }) });
                            }
                        }
                    }
                    return item;
                });
            });
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
        };
    }, [selectedTool, id, lock.positioning, setManipulative]);
    var handleClick = function (e) {
        var _a;
        // Selection and bring-to-front are handled in onMouseDown.
        // handleClick only fires on non-drag clicks; nothing extra needed here.
        // EXCEPT: iPad VoiceOver double-tap / keyboard Enter-Space synthesize a
        // click with `detail === 0`. onKeyDown catches desktop keyboard, but VO
        // double-tap does not fire keydown — only click. Open the frame's context
        // menu so AT users can reach Edit / Scale / Move / Lock / Delete.
        if (e.detail === 0 && !panningEnable) {
            e.preventDefault();
            e.stopPropagation();
            if (!selectedTool.includes(id))
                setSelectedTool([id]);
            var rect = (_a = frameInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
            }
            return;
        }
        e.stopPropagation();
    };
    // Handle button panel events (rotate, show5x5Grid, etc.)
    useEffect(function () {
        var handleBottomPanelEvent = function (event) {
            var _a, _b;
            var buttonId = event.id;
            // Only handle events when this frame is selected
            if (!selectedTool.includes(id))
                return;
            switch (buttonId) {
                case 'editNumberFrame':
                    // Always open in create mode — adds a new frame, never edits existing
                    setManiplulatorComponentVisible(true);
                    setActiveManipulator('numberFrames');
                    setSelectedManipulativeId('');
                    break;
                case 'rotate':
                case 'rotateClockwise':
                case 'rotateAnticlockwise': {
                    // `rotate` (left-toolbar) and `rotateClockwise` (context menu) both
                    // rotate +90°. `rotateAnticlockwise` rotates -90°.
                    var delta_1 = buttonId === 'rotateAnticlockwise' ? -90 : 90;
                    var newRotation = ((rotation + delta_1) % 360 + 360) % 360;
                    var clockwise_1 = delta_1 > 0;
                    // Rotate a point around the frame center by ±90° (screen coords).
                    var rotatePoint_1 = function (px, py) {
                        return clockwise_1
                            ? { x: center.x - (py - center.y), y: center.y + (px - center.x) }
                            : { x: center.x + (py - center.y), y: center.y - (px - center.x) };
                    };
                    var rotateBatch_1 = [
                        {
                            type: id,
                            id: id,
                            propertyName: 'rotation',
                            old: JSON.stringify(rotation),
                            new: JSON.stringify(newRotation),
                        },
                    ];
                    // Batch-save counter centers so locked counters undo correctly
                    manipulativeRef.current
                        .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === id; })
                        .forEach(function (m) {
                        var c = m.data;
                        var rotatedPos = rotatePoint_1(c.center.x, c.center.y);
                        rotateBatch_1.push({
                            type: c.id,
                            id: c.id,
                            propertyName: 'center',
                            old: JSON.stringify(c.center),
                            new: JSON.stringify(rotatedPos),
                        });
                    });
                    saveState(rotateBatch_1);
                    announce(t('text.annotationRotated', {
                        name: t('text.manipulativeNumberFrame'),
                        degrees: newRotation,
                        direction: clockwise_1
                            ? t('text.clockwise', { defaultValue: 'clockwise' })
                            : t('text.anticlockwise', { defaultValue: 'anticlockwise' }),
                    }));
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a, _b;
                            // Rotate the frame itself
                            if (item.data.id === id && item.data.type === 'numberFrames') {
                                var nf = item.data;
                                var currentRot = (_a = nf.rotation) !== null && _a !== void 0 ? _a : 0;
                                return __assign(__assign({}, item), { data: __assign(__assign({}, nf), { rotation: ((currentRot + delta_1) % 360 + 360) % 360 }) });
                            }
                            // Rotate all counters attached to this frame
                            if (item.data.type === 'numberFrameCounter') {
                                var counter = item.data;
                                if (counter.frameId === id) {
                                    var rotatedPosition = rotatePoint_1(counter.center.x, counter.center.y);
                                    var currentCounterRot = (_b = counter.rotation) !== null && _b !== void 0 ? _b : 0;
                                    var newCounterRot = ((currentCounterRot + delta_1) % 360 + 360) % 360;
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: rotatedPosition, rotation: newCounterRot }) });
                                }
                            }
                            return item;
                        });
                    });
                    break;
                }
                case 'show5x5Grid':
                    // Toggle 5x5 grid overlay
                    saveState({
                        type: id,
                        id: id,
                        propertyName: 'show5x5Grid',
                        old: JSON.stringify(show5x5Grid),
                        new: JSON.stringify(!show5x5Grid),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id && item.data.type === 'numberFrames') {
                                var nf = item.data;
                                return __assign(__assign({}, item), { data: __assign(__assign({}, nf), { show5x5Grid: !nf.show5x5Grid }) });
                            }
                            return item;
                        });
                    });
                    announce(t(show5x5Grid ? 'text.fiveGridHidden' : 'text.fiveGridShown'));
                    break;
                case 'scaleMinus': {
                    var newScale_1 = Math.max(0.5, parseFloat((scale - 0.1).toFixed(1)));
                    if (newScale_1 !== scale) {
                        var scaleBatch_1 = [
                            {
                                type: id,
                                id: id,
                                propertyName: 'scale',
                                old: JSON.stringify(scale),
                                new: JSON.stringify(newScale_1),
                            },
                        ];
                        // Build a scaled-frame snapshot so we can re-project each attached
                        // counter onto its cell at the new scale. Without this, locked
                        // counters keep stale `center` values and drift off their cells
                        // (unlocked counters mask the bug because Counter.tsx re-projects
                        // at render time via calculateTokenPosition).
                        var liveFrameForScale = (_a = manipulativeRef.current.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; })) === null || _a === void 0 ? void 0 : _a.data;
                        var scaledFrame_1 = liveFrameForScale
                            ? __assign(__assign({}, liveFrameForScale), { scale: newScale_1 }) : null;
                        var counterCenters_1 = new Map();
                        manipulativeRef.current
                            .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === id; })
                            .forEach(function (m) {
                            var _a, _b, _c, _d, _e, _f;
                            var c = m.data;
                            var defaultW = (_b = (_a = c.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                            var defaultH = (_d = (_c = c.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                            var cellIdxStr = scaledFrame_1
                                ? (_e = Object.entries(scaledFrame_1.tokens || {}).find(function (_a) {
                                    var v = _a[1];
                                    return v === c.id;
                                })) === null || _e === void 0 ? void 0 : _e[0]
                                : undefined;
                            var newCenter = scaledFrame_1 && cellIdxStr !== undefined
                                ? calculateTokenPosition(scaledFrame_1, parseInt(cellIdxStr))
                                : c.center;
                            counterCenters_1.set(c.id, newCenter);
                            scaleBatch_1.push({
                                type: c.id,
                                id: c.id,
                                propertyName: 'scale',
                                old: JSON.stringify((_f = c.scale) !== null && _f !== void 0 ? _f : 1),
                                new: JSON.stringify(newScale_1),
                            });
                            scaleBatch_1.push({
                                type: c.id,
                                id: c.id,
                                propertyName: 'dimensions',
                                old: JSON.stringify(c.dimensions),
                                new: JSON.stringify({ width: defaultW * newScale_1, height: defaultH * newScale_1 }),
                            });
                            scaleBatch_1.push({
                                type: c.id,
                                id: c.id,
                                propertyName: 'center',
                                old: JSON.stringify(c.center),
                                new: JSON.stringify(newCenter),
                            });
                        });
                        saveState(scaleBatch_1);
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                var _a, _b, _c, _d, _e;
                                if (item.data.id === id && item.data.type === 'numberFrames') {
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_1 }) });
                                }
                                if (item.data.type === 'numberFrameCounter') {
                                    var counter = item.data;
                                    if (counter.frameId === id) {
                                        var defaultW = (_b = (_a = counter.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                                        var defaultH = (_d = (_c = counter.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                                        return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: (_e = counterCenters_1.get(counter.id)) !== null && _e !== void 0 ? _e : counter.center, scale: newScale_1, dimensions: { width: defaultW * newScale_1, height: defaultH * newScale_1 } }) });
                                    }
                                }
                                return item;
                            });
                        });
                        announce(t('text.manipulativeScaled', {
                            name: t('text.manipulativeNumberFrame'),
                            direction: t('text.down', { defaultValue: 'down' }),
                            value: newScale_1.toFixed(1),
                        }));
                    }
                    break;
                }
                case 'scalePlus': {
                    var newScale_2 = parseFloat((scale + 0.1).toFixed(1));
                    var scaleBatch_2 = [
                        {
                            type: id,
                            id: id,
                            propertyName: 'scale',
                            old: JSON.stringify(scale),
                            new: JSON.stringify(newScale_2),
                        },
                    ];
                    // Same re-project pattern as scaleMinus — keeps locked counters
                    // snapped to their cells when the frame scales up.
                    var liveFrameForScalePlus = (_b = manipulativeRef.current.find(function (m) { return m.data.id === id && m.data.type === 'numberFrames'; })) === null || _b === void 0 ? void 0 : _b.data;
                    var scaledFramePlus_1 = liveFrameForScalePlus
                        ? __assign(__assign({}, liveFrameForScalePlus), { scale: newScale_2 }) : null;
                    var counterCentersPlus_1 = new Map();
                    manipulativeRef.current
                        .filter(function (m) { return m.data.type === 'numberFrameCounter' && m.data.frameId === id; })
                        .forEach(function (m) {
                        var _a, _b, _c, _d, _e, _f;
                        var c = m.data;
                        var defaultW = (_b = (_a = c.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                        var defaultH = (_d = (_c = c.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                        var cellIdxStr = scaledFramePlus_1
                            ? (_e = Object.entries(scaledFramePlus_1.tokens || {}).find(function (_a) {
                                var v = _a[1];
                                return v === c.id;
                            })) === null || _e === void 0 ? void 0 : _e[0]
                            : undefined;
                        var newCenter = scaledFramePlus_1 && cellIdxStr !== undefined
                            ? calculateTokenPosition(scaledFramePlus_1, parseInt(cellIdxStr))
                            : c.center;
                        counterCentersPlus_1.set(c.id, newCenter);
                        scaleBatch_2.push({
                            type: c.id,
                            id: c.id,
                            propertyName: 'scale',
                            old: JSON.stringify((_f = c.scale) !== null && _f !== void 0 ? _f : 1),
                            new: JSON.stringify(newScale_2),
                        });
                        scaleBatch_2.push({
                            type: c.id,
                            id: c.id,
                            propertyName: 'dimensions',
                            old: JSON.stringify(c.dimensions),
                            new: JSON.stringify({ width: defaultW * newScale_2, height: defaultH * newScale_2 }),
                        });
                        scaleBatch_2.push({
                            type: c.id,
                            id: c.id,
                            propertyName: 'center',
                            old: JSON.stringify(c.center),
                            new: JSON.stringify(newCenter),
                        });
                    });
                    saveState(scaleBatch_2);
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a, _b, _c, _d, _e;
                            if (item.data.id === id && item.data.type === 'numberFrames') {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_2 }) });
                            }
                            if (item.data.type === 'numberFrameCounter') {
                                var counter = item.data;
                                if (counter.frameId === id) {
                                    var defaultW = (_b = (_a = counter.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 50;
                                    var defaultH = (_d = (_c = counter.defaultDimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 50;
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, counter), { center: (_e = counterCentersPlus_1.get(counter.id)) !== null && _e !== void 0 ? _e : counter.center, scale: newScale_2, dimensions: { width: defaultW * newScale_2, height: defaultH * newScale_2 } }) });
                                }
                            }
                            return item;
                        });
                    });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.manipulativeNumberFrame'),
                        direction: t('text.up', { defaultValue: 'up' }),
                        value: newScale_2.toFixed(1),
                    }));
                    break;
                }
                default:
                    break;
            }
        };
        customEventHandler.addEventListener('bottomPanel', handleBottomPanelEvent);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleBottomPanelEvent);
        };
    }, [
        id,
        selectedTool,
        setManipulative,
        center,
        scale,
        rotation,
        show5x5Grid,
        setManiplulatorComponentVisible,
        setActiveManipulator,
        setSelectedManipulativeId,
    ]);
    // Render 5x5 grid overlay
    var render5x5Grid = function () {
        if (!show5x5Grid)
            return null;
        var lines = [];
        var border = GRID_BORDER_PX; // 2px — grid content starts here
        // Vertical lines every 5 cells
        for (var col = 5; col < cols; col += 5) {
            lines.push(_jsx("div", { className: "grid-line vertical", style: {
                    position: 'absolute',
                    left: border + col * CELL_SIZE,
                    top: border,
                    width: '2px',
                    height: rows * CELL_SIZE,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'none',
                } }, "v-".concat(col)));
        }
        // Horizontal lines every 5 cells
        for (var row = 5; row < rows; row += 5) {
            lines.push(_jsx("div", { className: "grid-line horizontal", style: {
                    position: 'absolute',
                    left: border,
                    top: border + row * CELL_SIZE,
                    width: cols * CELL_SIZE,
                    height: '2px',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'none',
                } }, "h-".concat(row)));
        }
        return _jsx("div", { className: "grid-5x5-overlay", children: lines });
    };
    // Hide move handle when a counter belonging to this frame is selected (being dragged)
    var isCounterOfThisFrameSelected = selectedTool.some(function (toolId) {
        return manipulative.some(function (m) { return m.data.id === toolId && m.data.type === 'numberFrameCounter' && m.data.frameId === id; });
    });
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: frameInteractiveRef, className: "numberFramesWidget ".concat(lock.positioning ? 'locked' : '', " ").concat(isSelected ? 'selected' : ''), "data-manipulative-id": id, role: "grid", "aria-roledescription": t('text.numberFrame'), "aria-label": (_e = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _e !== void 0 ? _e : t('text.numberFrameRowsCols', { rows: rows, cols: cols }), tabIndex: isSelected && !panningEnable ? 0 : -1, style: (_b = {
                        position: 'absolute',
                        left: center.x - outerWidth / 2,
                        top: center.y - outerHeight / 2,
                        width: outerWidth,
                        height: outerHeight,
                        transform: "rotate(".concat(rotation, "deg) scale(").concat(scale, ") translateZ(0)"),
                        transformOrigin: 'center center',
                        cursor: lock.positioning || panningEnable ? 'default' : dragStart ? 'grabbing' : 'grab'
                    },
                    _b['--cell-outline-width'] = scale < 0.7 ? '2px' : scale <= 0.9 ? '2px' : '1px',
                    _b['--nf-frame-border-width'] = scale < 0.7 ? '3px' : scale <= 0.9 ? '2px' : '2px',
                    // Exposed so handle buttons can counter-rotate their icons to stay
                    // visually upright regardless of frame rotation.
                    _b['--frame-rotation'] = "".concat(rotation, "deg"),
                    _b.zIndex = dragStart ? 9999 : undefined,
                    _b), "data-id": id, onMouseDown: onMouseDown, onTouchStart: onMouseDown, onClick: handleClick, onKeyDown: handleFrameKeyDown, onContextMenu: handleFrameContextMenu, children: [_jsx(SelectFocusOverlay, { id: id, hidden: panningEnable, className: "nfSelectBtn", showToolTip: false, onHoverChange: function (hovered, rect) {
                            setSelectBtnHovered(hovered);
                            if (hovered && rect)
                                setSelectBtnRect(rect);
                            else
                                setSelectBtnRect(null);
                        }, onAfterClick: function () {
                            setSelectBtnHovered(false);
                            setSelectBtnRect(null);
                            // No override — the dice-style wrapper auto-focus effect below
                            // will run once isSelected flips on and land focus on the frame
                            // widget (frameInteractiveRef). A second Enter then opens the
                            // frame's context menu.
                        } }), _jsx("div", { className: "grid", style: {
                            display: 'grid',
                            gridTemplateColumns: "repeat(".concat(cols, ", ").concat(CELL_SIZE, "px)"),
                            gridTemplateRows: "repeat(".concat(rows, ", ").concat(CELL_SIZE, "px)"),
                            width: '100%',
                            height: '100%',
                            padding: "".concat(GRID_PADDING, "px"),
                            boxSizing: 'border-box',
                        }, children: Array.from({ length: rows * cols }).map(function (_, i) {
                            var cellRow = Math.floor(i / cols);
                            var cellCol = i % cols;
                            var isShaded = show5x5Grid && (Math.floor(cellRow / 5) + Math.floor(cellCol / 5)) % 2 === 0;
                            return (_jsx("div", { className: "cell".concat(isShaded ? ' shaded' : '').concat(cellCol === cols - 1 ? ' last-col' : '').concat(cellRow === rows - 1 ? ' last-row' : ''), role: "gridcell", "aria-label": t('text.cellNumber', { index: i + 1 }), "data-cell-index": i }, i));
                        }) }), render5x5Grid(), isSelected && selectionIsAllFrames && !panningEnable && (_jsxs("div", { className: "nfRotateHandle", onMouseDown: handleRotateMouseDown, onTouchStart: handleRotateMouseDown, children: [_jsx(ButtonComponent, { defaultCSS: "nfRotateBtn".concat(rotateGrabbing ? ' nfRotateBtn--grabbing' : ''), ariaLabel: (_f = t('text.rotateHandle')) !== null && _f !== void 0 ? _f : 'Rotate frame', tabIndex: 0, noAudio: true, imgSrc: {
                                    normal: './assets/images/manipulative/patternShape/Rotate_N.svg',
                                    hover: './assets/images/manipulative/patternShape/Rotate_H.svg',
                                }, onMouseDown: handleRotateMouseDown, onTouchStart: handleRotateMouseDown, onKeyDown: handleRotateKeyDown, onKeyUp: handleRotateKeyUp }), _jsx("div", { className: "nfRotateLine", style: { width: scale < 0.7 ? '2px' : scale <= 0.9 ? '2px' : '1px' } })] })), isSelected &&
                        selectionIsAllFrames &&
                        !lock.positioning &&
                        !panningEnable &&
                        !isCounterOfThisFrameSelected && (_jsx(ButtonComponent, { defaultCSS: "nfMoveHandle", normalCSS: "nfMoveHandleBtn".concat(dragStart ? ' nfMoveHandleBtn--grabbing' : ''), ariaLabel: (_g = t('text.moveFrame')) !== null && _g !== void 0 ? _g : 'Move frame', tabIndex: -1, type: "imageType1", noAudio: true, imgSrc: (_h = btnConfig.moveNumberFrame) === null || _h === void 0 ? void 0 : _h.imgSrc, onMouseDown: function (e) {
                            e.stopPropagation();
                            onMouseDown(e);
                        }, onTouchStart: function (e) {
                            e.stopPropagation();
                            onMouseDown(e);
                        }, onMouseOver: function (e) {
                            setMoveBtnHovered(true);
                            setMoveBtnRect(e.currentTarget.getBoundingClientRect());
                        }, onMouseOut: function () {
                            setMoveBtnHovered(false);
                            setMoveBtnRect(null);
                        } }))] }), selectBtnHovered &&
                selectBtnRect &&
                !IS_TOUCH_DEVICE &&
                ReactDOM.createPortal(_jsx("div", { style: (_c = {
                            position: 'fixed',
                            top: selectBtnRect === null || selectBtnRect === void 0 ? void 0 : selectBtnRect.top,
                            left: selectBtnRect === null || selectBtnRect === void 0 ? void 0 : selectBtnRect.left,
                            width: selectBtnRect === null || selectBtnRect === void 0 ? void 0 : selectBtnRect.width,
                            height: selectBtnRect === null || selectBtnRect === void 0 ? void 0 : selectBtnRect.height,
                            pointerEvents: 'none',
                            // transform: `scale(${zoomLevel * scale})`,
                            // transformOrigin: 'top left',
                            zIndex: 99999
                        },
                        _c['--tooltipFontSize'] = "".concat(16 * zoomLevel * scale, "px"),
                        _c), children: _jsx(ToolTip, { buttonHovered: true, text: (_j = t('text.selectNumberFrame')) !== null && _j !== void 0 ? _j : 'Select number frame', direction: "bottom", 
                        // isParentRelative
                        useViewportPosition: true, isWhiteBoard: true, toolTipCSS: 'numberFrameTooltip' }) }), document.body), moveBtnHovered &&
                moveBtnRect &&
                !dragStart &&
                !IS_TOUCH_DEVICE &&
                ReactDOM.createPortal(_jsx("div", { style: (_d = {
                            position: 'fixed',
                            top: moveBtnRect.top,
                            left: moveBtnRect.left,
                            width: moveBtnRect.width,
                            height: moveBtnRect.height,
                            pointerEvents: 'none',
                            zIndex: 99999
                        },
                        _d['--tooltipFontSize'] = "".concat(16 * zoomLevel * scale, "px"),
                        _d), children: _jsx(ToolTip, { buttonHovered: true, text: (_k = t('text.dragFrame')) !== null && _k !== void 0 ? _k : 'Drag Frame', direction: "bottom", useViewportPosition: true, isWhiteBoard: true, toolTipCSS: 'numberFrameTooltip' }) }), document.body)] }));
};
export default NumberFrames;
